/**
 * Minimal app frame for self-employed (external) drivers: their runs, checks and defect reports only. On a phone it is
 * the mobile design's 3-tab shell (docs/mobile-design.md: Runs, Check, Report; Phase 3 chunk 2), same visual system as
 * the staff phone shell; on a computer the older compact frame.
 */
import { useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ClipboardCheck, LogOut, Truck, Wrench, type LucideIcon } from 'lucide-react';
import { EXTERNAL_DRIVER_TABS, externalDriverTab, type ExternalDriverTab } from '@tradeoutlet/shared';
import { useAuth } from '@/lib/auth';
import { useMe } from '@/lib/hooks';
import { BrandMark } from '@/components/brand/BrandMark';
import { Avatar } from '@/components/ui';
import { useMobileLayout } from '@/mobile/layout';

const TAB_ICON: Record<ExternalDriverTab, LucideIcon> = { runs: Truck, check: ClipboardCheck, report: Wrench };

export function DriverShell() {
  return useMobileLayout() ? <MobileDriverShell /> : <DesktopDriverShell />;
}

function DesktopDriverShell() {
  const { signOut } = useAuth();
  const me = useMe();
  const cls = ({ isActive }: { isActive: boolean }) => clsx('flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px]', isActive ? 'text-chrome-active' : 'text-white/70');
  return (
    <div className="flex h-full flex-col bg-slate-50 dark:bg-slate-950" data-testid="driver-shell">
      <header className="flex h-12 shrink-0 items-center gap-2 bg-chrome-bar px-4 text-white">
        <BrandMark height={26} />
        <span className="flex-1 truncate text-sm text-white/80">{me.data?.profile?.display_name} · Driver</span>
        <button type="button" onClick={() => void signOut()} className="flex items-center gap-1 text-xs text-white/70 hover:text-white"><LogOut className="h-3.5 w-3.5" />Sign out</button>
      </header>
      <main className="min-h-0 flex-1 overflow-y-auto"><Outlet /></main>
      <nav className="flex shrink-0 bg-chrome-bar">
        <NavLink to="/fleet/my-runs" className={cls}><Truck className="h-5 w-5" />My runs</NavLink>
        <NavLink to="/fleet/check" className={cls}><ClipboardCheck className="h-5 w-5" />Walkaround</NavLink>
        <NavLink to="/fleet/report" className={cls}><Wrench className="h-5 w-5" />Report</NavLink>
      </nav>
    </div>
  );
}

/** Phone: top bar (who, Sign out), the page, and the black tab bar with Runs, Check and Report. */
function MobileDriverShell() {
  const { signOut } = useAuth();
  const me = useMe().data?.profile;
  const { pathname } = useLocation();
  const nav = useNavigate();
  const active = externalDriverTab(pathname);
  // Inputs 16px+ while the phone shell is up (index.css), so iOS Safari doesn't zoom on focus.
  useEffect(() => { document.documentElement.classList.add('mobile'); return () => document.documentElement.classList.remove('mobile'); }, []);
  return (
    <div className="mobile-shell flex h-full flex-col overflow-hidden bg-white dark:bg-slate-950" data-testid="driver-shell">
      <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 pt-[env(safe-area-inset-top)] dark:border-slate-800 dark:bg-slate-950" data-testid="driver-topbar">
        <span className="flex min-h-14 min-w-0 flex-1 items-center gap-2">
          <Avatar size={32} name={me?.display_name} id={me?.user_id} url={me?.avatar_url} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">{me?.display_name ?? 'Driver'}</span>
            <span className="block text-xs text-slate-500">Self-employed driver</span>
          </span>
        </span>
        <button type="button" onClick={() => void signOut()} className="flex h-11 items-center gap-1 rounded-full px-3 text-sm font-medium text-slate-600 active:bg-slate-100 dark:text-slate-300 dark:active:bg-slate-800" data-testid="driver-signout">
          <LogOut className="h-4 w-4" aria-hidden />Sign out
        </button>
      </header>
      <main id="main" tabIndex={-1} className="relative flex min-h-0 flex-1 outline-none">
        <div className="min-w-0 flex-1 overflow-hidden"><Outlet /></div>
      </main>
      <nav className="on-dark flex shrink-0 items-stretch bg-chrome-bar px-1 pb-[env(safe-area-inset-bottom)]" aria-label="Main" data-testid="driver-tabbar">
        {EXTERNAL_DRIVER_TABS.map(t => {
          const Icon = TAB_ICON[t.id];
          const on = active === t.id;
          return (
            <button key={t.id} type="button" data-testid={`driver-tab-${t.id}`} aria-current={on ? 'page' : undefined} onClick={() => nav(t.to)}
              className={clsx('relative flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-xs font-medium', on ? 'text-white' : 'text-white/70')}>
              <span className={clsx('flex h-7 w-12 items-center justify-center rounded-full', on && 'bg-accent text-on-accent')}><Icon className="h-5 w-5" /></span>
              <span className="truncate">{t.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
